/**
 * Dialog for saving current filter configuration as a preset.
 */

import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useFilterStore } from '@/stores/filterStore'
import { useStudyStore } from '@/stores/studyStore'
import { useSavedFilters } from '@/hooks/useFilters'
import { useAuthContext } from '@/hooks/AuthContext'

interface SaveFilterDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pageKey?: string
  onSaved?: () => void
}

export function SaveFilterDialog({
  open,
  onOpenChange,
  pageKey,
  onSaved,
}: SaveFilterDialogProps) {
  const { user } = useAuthContext()
  const currentStudy = useStudyStore((state) => state.currentStudy)
  const globalFilters = useFilterStore((state) => state.globalFilters)
  const localFilters = useFilterStore((state) => state.localFilters)

  const { createFilter, isCreating } = useSavedFilters(
    currentStudy?.studyId || null,
    pageKey
  )

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [pageSpecific, setPageSpecific] = useState(false)
  const [isShared, setIsShared] = useState(false)
  const [isDefault, setIsDefault] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isAdmin = user?.roles?.some(
    (r) => r === 'system_admin' || r === 'study_admin'
  )

  // Check if there are any active filters to save
  const hasActiveFilters = () => {
    const hasGlobal = Object.values(globalFilters).some(
      (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
    )
    const hasLocal = Object.values(localFilters).some((page) =>
      Object.values(page).some(
        (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
      )
    )
    return hasGlobal || hasLocal
  }

  const handleSave = () => {
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    if (!hasActiveFilters()) {
      setError('No filters to save')
      return
    }

    setError(null)

    // Build filter definition - strip null values for API compatibility
    const cleanGlobalFilters: Record<string, string | string[] | { start?: string; end?: string }> = {}
    for (const [key, value] of Object.entries(globalFilters)) {
      if (value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        cleanGlobalFilters[key] = value as string | string[] | { start?: string; end?: string }
      }
    }

    const cleanLocalFilters: Record<string, Record<string, string | string[]>> = {}
    const localFiltersToSave = pageSpecific && pageKey ? { [pageKey]: localFilters[pageKey] || {} } : localFilters
    for (const [pageK, pageFilters] of Object.entries(localFiltersToSave)) {
      const cleanPage: Record<string, string | string[]> = {}
      for (const [key, value] of Object.entries(pageFilters)) {
        if (value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
          cleanPage[key] = value as string | string[]
        }
      }
      if (Object.keys(cleanPage).length > 0) {
        cleanLocalFilters[pageK] = cleanPage
      }
    }

    const filterDefinition = {
      globalFilters: cleanGlobalFilters,
      localFilters: cleanLocalFilters,
    }

    createFilter(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        filter_definition: filterDefinition,
        page_key: pageSpecific && pageKey ? pageKey : undefined,
        is_shared: isShared,
        is_default: isDefault,
      },
      {
        onSuccess: () => {
          // Reset form
          setName('')
          setDescription('')
          setPageSpecific(false)
          setIsShared(false)
          setIsDefault(false)
          onOpenChange(false)
          onSaved?.()
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to save filter')
        },
      }
    )
  }

  const handleClose = () => {
    setName('')
    setDescription('')
    setPageSpecific(false)
    setIsShared(false)
    setIsDefault(false)
    setError(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Filter Preset</DialogTitle>
          <DialogDescription>
            Save your current filter configuration for quick access later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="filter-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="filter-name"
              placeholder="e.g., Active AEs Only"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="filter-description">Description</Label>
            <Input
              id="filter-description"
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Options */}
          <div className="space-y-3">
            {pageKey && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="page-specific"
                  checked={pageSpecific}
                  onCheckedChange={(checked) => setPageSpecific(checked === true)}
                />
                <Label htmlFor="page-specific" className="text-sm font-normal cursor-pointer">
                  Save for this page only
                </Label>
              </div>
            )}

            {isAdmin && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is-shared"
                  checked={isShared}
                  onCheckedChange={(checked) => setIsShared(checked === true)}
                />
                <Label htmlFor="is-shared" className="text-sm font-normal cursor-pointer">
                  Share with team (visible to all users)
                </Label>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-default"
                checked={isDefault}
                onCheckedChange={(checked) => setIsDefault(checked === true)}
              />
              <Label htmlFor="is-default" className="text-sm font-normal cursor-pointer">
                Set as my default filter
              </Label>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          {/* Active filters summary */}
          {!hasActiveFilters() && (
            <p className="text-sm text-muted-foreground">
              No active filters to save. Apply some filters first.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isCreating || !hasActiveFilters()}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Filter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
